/**
 * Configuration Helper Utilities
 *
 * Provides functions for:
 * - Category derivation from configurations
 * - Preview text generation
 * - Default value initialization
 * - Backward compatibility helpers
 */

import { CONFIGURATIONS, getConfigurationsByCategory, getUniqueCategories, getConfigurationById } from "../config/configurations";
import { getCategoryDisplay, getSortedCategories } from "../config/categories";

/**
 * Get all categories with their configurations and aggregated preview text
 *
 * @param {Object} taskConfig - Current task configuration state
 * @returns {Array} Array of category objects with configItems and aggregated preview
 */
export const getCategoriesWithConfigs = (taskConfig) => {
  const categoryIds = getUniqueCategories();
  const sortedCategories = getSortedCategories(categoryIds);

  return sortedCategories.map((categoryDisplay) => {
    const configItems = getConfigurationsByCategory(categoryDisplay.id, taskConfig?.automationType);

    // Generate aggregated preview for the category
    const previews = configItems
      .map((item) => {
        const config = taskConfig?.[item.id];
        // Only include preview if config has values
        if (!config || Object.keys(config).length === 0) return null;
        return item.getPreview(config, taskConfig?.automationType);
      })
      .filter(Boolean);

    const aggregatedPreview = previews.length > 0 ? previews.join(" | ") : categoryDisplay.description;

    // Check if any config in this category has been modified
    const hasConfig = configItems.some((item) => {
      const config = taskConfig?.[item.id];
      return config && Object.keys(config).length > 0;
    });

    return {
      ...categoryDisplay,
      configItems,
      preview: aggregatedPreview,
      hasConfig,
    };
  }).filter((category) => category.configItems.length > 0);
};

/**
 * Build default task config from all configuration definitions
 *
 * @param {string|null} automationType - Optional automation type to set
 * @returns {Object} Default task configuration
 */
export const buildDefaultTaskConfig = (automationType = null) => {
  const config = {
    automationType,
    metadata: {
      title: "",
      websiteUrl: "",
      websiteFavicon: "",
    },
    urls: [],
  };

  // Add default config for each configuration item
  CONFIGURATIONS.forEach((item) => {
    config[item.id] = { ...item.defaultConfig };
  });

  return config;
};

/**
 * Get configuration options for display in CloudTaskSetupView step 2
 * This is for backward compatibility during migration
 *
 * @param {string} automationType - The automation type
 * @returns {Array} Array of configuration options
 */
export const getConfigurationOptions = (automationType) => {
  return CONFIGURATIONS.filter((c) => c.category === "core" || c.category === "afterRun")
    .sort((a, b) => {
      // Core items first, then by order
      if (a.category === "core" && b.category !== "core") return -1;
      if (b.category === "core" && a.category !== "core") return 1;
      return a.order - b.order;
    })
    .map((item) => ({
      id: item.id,
      label: item.label,
      emoji: item.emoji,
      description: item.description,
      required: item.required,
      automationType: item.id === "automation" ? automationType : undefined,
    }));
};

/**
 * Get preview text for a specific configuration
 *
 * @param {string} configId - Configuration ID
 * @param {Object} config - Configuration values
 * @param {string} automationType - Automation type (for context)
 * @returns {string} Preview text
 */
export const getConfigPreview = (configId, config, automationType) => {
  const configItem = getConfigurationById(configId);
  if (configItem && configItem.getPreview) {
    return configItem.getPreview(config, automationType);
  }
  return "Configure this option";
};

/**
 * Validate task config against configuration requirements
 *
 * @param {Object} taskConfig - Task configuration to validate
 * @returns {Array} Array of validation errors (empty if valid)
 */
export const validateTaskConfig = (taskConfig) => {
  const errors = [];

  CONFIGURATIONS.forEach((item) => {
    if (item.required) {
      const config = taskConfig?.[item.id];
      if (!config || Object.keys(config).length === 0) {
        errors.push({
          field: item.id,
          message: `${item.label} is required`,
        });
      }
    }
  });

  return errors;
};
